/**
 * Classify admin table columns and cap wrapping text at ~4 lines.
 * Compact columns (#, dates, status, counts) stay nowrap; remarks-like
 * columns get a larger min-width. Native title shows full text on hover.
 */
const SKIP = '.sip-pager, .modal, [data-sip-widget], .sip-dash-widget, .sip-home, .nk-header, .sip-admin-header';

const KIND_CLASS = {
    serial: 'sip-col-serial',
    compact: 'sip-col-compact',
    text: 'sip-col-text'
};

const KIND_CLASSES = Object.values(KIND_CLASS);

function headerCells(table) {
    const row = table.querySelector('thead tr');
    return row ? Array.from(row.children) : [];
}

function shouldSkip(table) {
    if (!table || table.closest(SKIP)) {
        return true;
    }
    if (!table.closest('.table-responsive, .nk-tb-list, .datatable-wrap, .card')) {
        return true;
    }
    return false;
}

function headerKey(th) {
    return (th.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function classify(label) {
    const s = (label || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!s || s === '#' || /^(s\.?\s*no\.?|sr\.?n?o?\.?|serial|sl\.?)$/.test(s)) {
        return 'serial';
    }
    if (/(remark|description|address|\bnotes?\b|comment|message|narration|particulars)/.test(s)) {
        return 'text';
    }
    if (
        /\bdate\b|\bstatus\b|\bgender\b|\bdob\b|\baction\b|\bcount\b|no of students|number of|\bqty\b|\bcode\b|created at|updated at|created by/.test(s)
        || /\badm\.?\s*no|admission number|roll number/.test(s)
        || /^(employee|designation)$/.test(s)
        || /\bstatus$/.test(s)
    ) {
        return 'compact';
    }
    return 'body';
}

const ROLE_CLASSES = [
    'sip-cell-status',
    'sip-cell-remarks',
    'sip-cell-compact',
    'sip-cell-title-suffix',
    'sip-cell-empty',
    'sip-cell-meta'
];

function cellRole(label, kind) {
    const s = (label || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (kind === 'serial') {
        return null;
    }
    if (/\bstatus\b/.test(s)) {
        return 'sip-cell-status';
    }
    if (/(remark|description|address|\bnotes?\b|comment|message|narration|particulars)/.test(s)) {
        return 'sip-cell-remarks';
    }
    if (/\blast name\b|\bmiddle name\b|surname/.test(s)) {
        return 'sip-cell-title-suffix';
    }
    if (kind === 'compact') {
        return 'sip-cell-compact';
    }
    return 'sip-cell-meta';
}

function isEmptyCell(td) {
    if (td.querySelector('.badge, .btn, input, select, textarea, img, .sip-row-actions, .doc-tag-chip, .custom-control')) {
        return false;
    }
    const text = (td.textContent || '').replace(/\s+/g, ' ').trim();
    return !text || text === '-' || text === '—' || text === '–' || /^n\/?a$/i.test(text);
}

function existingKind(el) {
    if (!el || !el.classList) {
        return null;
    }
    if (el.classList.contains('sip-th-action') || el.classList.contains('table-action') || el.classList.contains('table-option') || el.classList.contains('nk-tb-col-tools')) {
        return 'action';
    }
    if (el.classList.contains('sip-col-serial')) {
        return 'serial';
    }
    if (el.classList.contains('sip-col-text') || el.classList.contains('remarks') || el.classList.contains('description')) {
        return 'text';
    }
    if (el.classList.contains('sip-col-compact')) {
        return 'compact';
    }
    return null;
}

function setKindClass(el, kind) {
    KIND_CLASSES.forEach(function (cls) {
        el.classList.remove(cls);
    });
    if (KIND_CLASS[kind]) {
        el.classList.add(KIND_CLASS[kind]);
    }
}

function isActionCell(td) {
    return !!(
        td.classList.contains('table-action')
        || td.classList.contains('table-option')
        || td.classList.contains('nk-tb-col-tools')
        || td.classList.contains('sip-th-action')
        || td.querySelector('.sip-row-actions, .btn-group, .dropdown, input, select, textarea, button, .custom-control')
    );
}

function cellPlainText(td) {
    return (td.getAttribute('title') || td.textContent || '').replace(/\s+/g, ' ').trim();
}

function wrapTextIfNeeded(td, full) {
    if (td.querySelector('.sip-td-clip, .sip-td-wrap, .sip-td-compact')) {
        return;
    }
    const meaningful = Array.from(td.childNodes).filter(function (node) {
        if (node.nodeType === 3) {
            return !!node.textContent && !!node.textContent.trim();
        }
        if (node.nodeType === 8) {
            return false;
        }
        return true;
    });
    if (meaningful.length !== 1 || meaningful[0].nodeType !== 3) {
        return;
    }
    const textNode = meaningful[0];
    const span = document.createElement('span');
    span.className = 'sip-td-clip';
    if (full) {
        span.setAttribute('title', full);
    }
    td.insertBefore(span, textNode);
    span.appendChild(textNode);
}

function decorateCell(td, kind) {
    if (kind === 'action' || isActionCell(td)) {
        return;
    }
    const full = cellPlainText(td);
    if (full) {
        if (!td.getAttribute('title')) {
            td.setAttribute('title', full);
        }
        const inner = td.querySelector('.sip-td-clip, .sip-td-wrap, .sip-td-compact');
        if (inner && !inner.getAttribute('title')) {
            inner.setAttribute('title', full);
        }
    }
    if (kind === 'serial' || kind === 'compact') {
        return;
    }
    wrapTextIfNeeded(td, full);
}

function applyColumnClasses(table) {
    const tableId = table.getAttribute('data-table-id');
    if (tableId) {
        table.classList.add('sip-list-' + tableId);
    }

    if (table.classList.contains('custom-show-table')) {
        table.querySelectorAll('tbody td:last-child').forEach(function (td) {
            decorateCell(td, 'text');
        });
        return;
    }

    const ths = headerCells(table);
    if (ths.length < 2) {
        table.querySelectorAll('tbody td').forEach(function (td) {
            decorateCell(td, 'body');
        });
        return;
    }

    const labels = ths.map(function (th) {
        if (th.classList.contains('select-all') || th.querySelector('input[type="checkbox"]')) {
            return '';
        }
        return (th.textContent || '').replace(/\s+/g, ' ').trim();
    });

    const kinds = ths.map(function (th) {
        return existingKind(th) || classify(headerKey(th));
    });

    let primaryPromoted = false;
    kinds.forEach(function (kind, i) {
        if (primaryPromoted || kind === 'serial' || kind === 'action' || kind === 'compact') {
            return;
        }
        if (kind === 'body') {
            kinds[i] = 'text';
        }
        primaryPromoted = true;
    });

    ths.forEach(function (th, i) {
        if (kinds[i] === 'action') {
            return;
        }
        setKindClass(th, kinds[i]);
    });

    Array.from(table.tBodies).forEach(function (tbody) {
        Array.from(tbody.rows).forEach(function (tr) {
            if (tr.cells.length === 1 && tr.cells[0].getAttribute('colspan')) {
                tr.cells[0].classList.add('sip-cell-colspan');
                return;
            }

            let firstDataAssigned = false;

            Array.from(tr.cells).forEach(function (td, i) {
                const kind = i < kinds.length ? kinds[i] : 'body';
                const isCheckbox = td.classList.contains('select-all') || !!td.querySelector('input[type="checkbox"]');
                const isAction = kind === 'action' || isActionCell(td) || (i === tr.cells.length - 1 && (td.classList.contains('table-action') || td.classList.contains('nk-tb-col-tools') || !!td.querySelector('.sip-row-actions, .btn-group, .dropdown, [role="menu"]')));

                ROLE_CLASSES.forEach(function (cls) {
                    if (cls !== 'sip-cell-empty') {
                        td.classList.remove(cls);
                    }
                });

                if (isCheckbox) {
                    td.classList.add('sip-cell-checkbox');
                    return;
                }

                if (isAction) {
                    td.classList.add('sip-cell-action');
                    return;
                }

                const rawLabel = i < labels.length ? labels[i] : '';
                if (rawLabel && !td.getAttribute('data-label')) {
                    td.setAttribute('data-label', rawLabel);
                }

                if (isEmptyCell(td)) {
                    td.classList.add('sip-cell-empty');
                } else {
                    td.classList.remove('sip-cell-empty');
                }

                const role = cellRole(rawLabel, kind);
                if (role && !(role === 'sip-cell-meta' && td.classList.contains('sip-cell-inline'))) {
                    td.classList.add(role);
                }

                if (!firstDataAssigned && !td.classList.contains('sip-cell-empty') && kind !== 'serial' && kind !== 'compact' && role !== 'sip-cell-title-suffix' && role !== 'sip-cell-status') {
                    td.classList.add('sip-cell-primary');
                    td.classList.remove('sip-cell-meta');
                    firstDataAssigned = true;
                }

                if (!existingKind(td)) {
                    setKindClass(td, kind);
                }
                decorateCell(td, existingKind(td) || kind);
            });

            if (!firstDataAssigned) {
                const fallback = Array.from(tr.cells).find(function (td) {
                    return !td.classList.contains('sip-col-serial')
                        && !td.classList.contains('sip-cell-checkbox')
                        && !td.classList.contains('sip-cell-action')
                        && !td.classList.contains('sip-cell-empty');
                });
                if (fallback) {
                    fallback.classList.add('sip-cell-primary');
                }
            }
        });
    });
}

export function attachTableCells(table) {
    if (shouldSkip(table)) {
        return;
    }
    applyColumnClasses(table);
}

export function scanTableCells(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.table-responsive table.table, .datatable-wrap table.table, .card > .table, .card table.table').forEach(attachTableCells);
}

export default {
    bind: attachTableCells,
    inserted: attachTableCells,
    componentUpdated: attachTableCells
};
